<template>
  <div>
    <div v-if="hasPermissionToEdit">
      <EsTextarea
        v-model="newNote"
        placeholder="Add a note..." 
        ref="noteTextarea" />
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="6" lg="3" v-if="noteBeingEdited">
          <EsButtonCancel
            :disabled="isSaving"
            block
            @click="cancelEdit()" />
        </v-col>
        <v-col :cols="noteBeingEdited ? 6 : 12" lg="3">
          <EsButtonSave
            :disabled="newNote.trim().length < 1"
            :loading="isSaving"
            block
            @click="saveNote()" />
        </v-col>
      </v-row>
    </div>
    <div class="my-10">
      <v-row
        v-for="(note, index) in sortedNotes"
        :key="note.id"
        :class="{'editing-note': noteBeingEdited === note.id}">
        <v-col cols="12" lg="3" :class="{'py-0': isMobile}">
          {{(note.createdByUser || {}).name || 'N/A'}}
        </v-col>
        <v-col cols="12" lg="2">
          {{formattedDate(note.createdAt)}}
        </v-col>
        <v-col>
          <div v-html="formattedNote(note.content)"></div>
        </v-col>
        <v-col cols="auto" v-if="hasPermissionToEdit">
          <v-row>
            <v-col cols="auto">
              <EsButtonIcon icon="mdi-pin"
                @click="pinNote(note.id)"
                :disabled="isPinning"
                :color="note.isPinned ? 'primary' : 'eightySixGrey1'"
                aria-label="pin note" />
            </v-col>
            <v-col cols="auto">
              <EsButtonIcon icon="mdi-pencil"
                @click="editNote(note.id)"
                aria-label="edit note" />
            </v-col>
            <v-col cols="auto">
              <EsButtonIcon icon="mdi-delete"
                @click="confirmDelete(note.id)"
                aria-label="delete note" />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" v-if="index < (notes.length - 1)">
          <v-divider></v-divider>
        </v-col>
      </v-row>
    </div>
    <v-dialog v-if="showDialog" v-model="showDialog" width="500">
      <v-card>
        <v-card-text class="pt-5">
          Are you sure you want to delete this note?
        </v-card-text>
        <v-card-actions>
          <v-row>
            <v-col>
              <EsButtonCancel block @click="showDialog = false" />
            </v-col>
            <v-col>
              <EsButtonSave
                block
                label="Yes, Delete"
                @click="deleteNote(noteToDelete)"
                :loading="isSaving" />
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  EsTextarea,
  EsButtonSave,
  EsButtonIcon,
  EsButtonCancel,
} from "@86-repairs/design-system";
import dayjs from 'dayjs';

export default {
  name: 'EsNotes',
  components: {
    EsTextarea,
    EsButtonSave,
    EsButtonCancel,
    EsButtonIcon,
  },
  props: {
    notes: {
      type: Array,
    },
    hasPermissionToEdit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    newNote: '',
    newNoteIsPinned: false,
    isSaving: false,
    isPinning: false,
    noteBeingEdited: null,
    noteToDelete: null,
    showDialog: false,
  }),
  computed: {
    sortedNotes() {
      return this.notes?.length > 0 ? [...this.notes].sort((a, b) => a.createdAt < b.createdAt ? 1 : -1) : [];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    formattedDate(date) {
      return dayjs(date).format('MM/DD/YYYY, hh:mm A');
    },
    formattedNote(text) {
      return text.replace(/\n/g, '<br>');
    },
    saveNote() {
      this.isSaving = true;
      this.$emit("saveNote", {
        payload: { content: this.newNote.trim(), isPinned: this.newNoteIsPinned },
        done: () => this.doneSaving(),
      });
      if (this.noteBeingEdited) {
        this.$emit("deleteNote", {
          payload: this.noteBeingEdited,
          done: () => this.doneDeleting(),
        });
      }
    },
    editNote(id) {
      this.noteBeingEdited = id;
      const noteToEdit = this.sortedNotes.find((note) => note.id === id);
      this.newNote = noteToEdit.content;
      this.newNoteIsPinned = noteToEdit.isPinned;
      this.$vuetify.goTo(this.$refs.noteTextarea, { duration: 300, offset: 0, easing: 'easeInOutCubic' })
    },
    cancelEdit() {
      this.newNote = '';
      this.newNoteIsPinned = false;
      this.noteBeingEdited = null;
    },
    confirmDelete(id) {
      this.noteToDelete = id;
      this.showDialog = true;
    },
    deleteNote(id) {
      this.isSaving = true;
      this.$emit("deleteNote", {
        payload: id,
        done: () => this.doneDeleting(),
      });
    },
    pinNote(noteId) {
      this.isPinning = true;
      const noteToUnpin = this.notes.find((note) => note.isPinned)?.id;
      this.$emit("pinNote", {
        noteToPin: noteToUnpin === noteId ? undefined : noteId,
        noteToUnpin,
        done: () => this.donePinning(),
      });
    },
    doneSaving() {
      this.isSaving = false;
      this.newNote = '';
      this.newNoteIsPinned = false;
    },
    doneDeleting() {
      this.isSaving = false;
      this.noteBeingEdited = null;
      this.showDialog = false;
      this.noteToDelete = null;
    },
    donePinning() {
      this.isPinning = false;
    }
  }
}
</script>

<style scoped>
  .editing-note {
    opacity: 0.3;
    pointer-events: none;

  }
</style>